import type {
  UseMutationOptions,
  UseQueryOptions,
  UseSuspenseQueryOptions,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";

import type { AnyJsonData, IQuery, JsonDocument } from "@citrine/client";
import { RefreshClient } from "@citrine/client";
import { queryClient } from "@citrine/client/utils";

export interface Pagination extends IQuery {
  "page[number]"?: number;
  "page[size]"?: number;
}

/**
 * JsonApiClient is based on the JSON:API data format
 * https://jsonapi.org/format/
 *
 * The primary distinction between this client and the conventional QueryClient
 * is that get() and find() are not assumed to contain identical data, and thus
 * find() results are not duplicated in the cache to be reused by get()
 *
 * This allows a find() response to be smaller (without included objects) while
 * a get() response contains more detailed data.
 */
export abstract class JsonApiClient<
  Model extends AnyJsonData = AnyJsonData,
  Included extends AnyJsonData | undefined = undefined,
  QueryParams extends Pagination = Pagination,
> extends RefreshClient {
  protected readonly endpoint: string;
  abstract readonly type: string;

  protected constructor(endpoint: string) {
    super();
    this.endpoint = endpoint;
  }

  /**
   * DELETE /api/v${version}/${type}/${id}
   */
  async delete(id: string) {
    const result = await this.request<void>({
      method: "DELETE",
      url: `${this.endpoint}/${id}`,
    });
    queryClient.setQueryData(this.getModelCacheKey(id), null);
    queryClient.invalidateQueries({
      queryKey: this.getQueryCacheKeyPrefix(),
    });
    return result;
  }

  deleteMutationOptions(
    options?: Omit<UseMutationOptions<void, AxiosError, string>, "mutationFn">,
  ): UseMutationOptions<void, AxiosError, string> {
    return {
      ...options,
      mutationFn: (id) => this.delete(id),
    };
  }

  /**
   * GET /api/v${version}/${type}
   */
  find(query?: QueryParams) {
    return this.request<JsonDocument<Model[]>>({
      method: "GET",
      url: this.endpoint,
      params: this.normalizeQuery(query),
    });
  }

  findQueryOptions(
    query?: QueryParams,
    options?: Omit<
      UseQueryOptions<JsonDocument<Model[]>, AxiosError>,
      "queryKey" | "queryFn"
    >,
  ): UseQueryOptions<JsonDocument<Model[]>, AxiosError> &
    UseSuspenseQueryOptions<JsonDocument<Model[]>, AxiosError> {
    return {
      ...options,
      queryKey: [
        ...this.getQueryCacheKeyPrefix(),
        String(this.normalizeQuery(query)),
      ],
      queryFn: () => this.find(query),
    };
  }

  /**
   * GET /api/v${version}/${type}/${id}
   */
  get(id: string) {
    return this.request<JsonDocument<Model, Included>>({
      method: "GET",
      url: `${this.endpoint}/${id}`,
    });
  }

  getQueryOptions(
    id: string,
    options?: Omit<
      UseQueryOptions<JsonDocument<Model, Included>, AxiosError>,
      "queryKey" | "queryFn"
    >,
  ): UseQueryOptions<JsonDocument<Model, Included>, AxiosError> &
    UseSuspenseQueryOptions<JsonDocument<Model, Included>, AxiosError> {
    return {
      ...options,
      queryKey: this.getModelCacheKey(id),
      queryFn: () => this.get(id),
    };
  }

  protected getModelCacheKey(id: string) {
    return ["json-api", `${this.type}/${id}`];
  }

  protected getQueryCacheKeyPrefix() {
    return ["json-api", this.type];
  }

  protected normalizeQuery(query?: QueryParams) {
    const params = new URLSearchParams(
      Object.entries(query || {})
        .filter(([, value]) => value != null)
        .map(([key, value]) => [key, String(value)]),
    );
    params.sort();
    return params;
  }
}
